import classNames from 'classnames';
import { SetStateAction, useCallback } from 'react';
import { useRegisterClassListInfinityQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';

interface SelectRegisterClassProps {
  registerId: string;
  setRegisterClass: (value: SetStateAction<SelectPaginateOption<number> | undefined>) => void;
  onChange?: (value: number | null) => void;
  registerClass: SelectPaginateOption<number> | undefined;
  isClearable?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
}

export const SelectRegisterClass = ({
  registerId,
  onChange,
  setRegisterClass,
  registerClass,
  isClearable,
  label = 'Class',
  placeholder,
  className,
}: SelectRegisterClassProps) => {
  const { fetchNextPage } = useRegisterClassListInfinityQuery(
    {
      page: 1,
      page_size: 15,
      register_id: +registerId,
    },
    true,
  );

  const handleOnAsyncRegisterClass = useCallback(
    async (page: number, search?: string) => {
      const classesData = await fetchNextPage({ pageParam: { page, search } });

      const classes = classesData?.data?.pages?.at(-1)?.data || [];
      return classes.map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      }));
    },
    [fetchNextPage],
  );

  const handleChangeRegisterClass = (options: SelectPaginateOption<number>) => {
    setRegisterClass(options);
    onChange && onChange(options?.value || null);
  };

  return (
    <div
      className={classNames(
        'flex items-center border px-2 rounded-lg gap-2 w-full font-[Inter] text-sm leading-[22px]',
        {
          'min-w-[280px]': !className?.includes('min-w-'),
          'max-w-[380px]': !className?.includes('max-w-'),
        },
        className,
      )}
    >
      {label && (
        <span className="border-r pr-2 w-1/2 h-[40px] text-center flex items-center justify-start text-gray-600">
          {label}
        </span>
      )}
      <span className="pl-2 w-full">
        <SelectPaginate
          key={registerId}
          disabled={!registerId}
          onChange={handleChangeRegisterClass}
          onAsync={handleOnAsyncRegisterClass}
          value={registerClass}
          showBorderBottom={false}
          isClearable={isClearable}
          placeholder={placeholder}
        />
      </span>
    </div>
  );
};
